import type NonEmptyText from '../non-empty-text';
import type Currency from './currency';
import type Customer from './customer';
import type OrderType from './order-type';
import type Stakeholder from './stakeholder';
import type Status from './status';
import type Tag from './tag';
import type Totals from './totals';
import type Workflow from './workflow';
import { assertIsInstanceOfArray, assertIsInstanceOfDate, assertIsInstanceOfNonEmptyText, assertIsInstanceOfOrderType, assertIsInstanceOfWorkflow } from '../assertions';
import { assertIsInstanceOfCurrency, assertIsInstanceOfCustomer, assertIsInstanceOfStatus, assertIsInstanceOfTotals } from './assertions';

interface IOrder {
	readonly currency: Currency;
	readonly customer: Customer;
	readonly orderDate: Date;
	readonly orderNumber: NonEmptyText;
	readonly orderType: OrderType;
	readonly stakeholders: Stakeholder[];
	readonly status: Status;
	readonly tags: Tag[];
	readonly totals: Totals;
	readonly workflow: Workflow;
}

export default class Order implements IOrder {
	public get currency(): Currency {
		return this.#currency;
	}

	public get customer(): Customer {
		return this.#customer;
	}

	public get orderDate(): Date {
		return this.#orderDate;
	}

	/** Return the orderDate.value Date type as danish formatted string with the following format dd.mm.yyyy */
	public get orderDateAsLocalized(): string {
		return new Intl.DateTimeFormat('da-DK', {
			dateStyle: 'short',
		}).format(this.orderDate);
	}

	public get orderNumber(): NonEmptyText {
		return this.#orderNumber;
	}

	public get orderType(): OrderType {
		return this.#orderType;
	}

	public get stakeholders(): Stakeholder[] {
		return this.#stakeholders;
	}

	public get status(): Status {
		return this.#status;
	}

	public get tags(): Tag[] {
		return this.#tags;
	}

	public get totals(): Totals {
		return this.#totals;
	}

	public get workflow(): Workflow {
		return this.#workflow;
	}

	#currency: Currency;
	#customer: Customer;
	#orderDate: Date;
	#orderNumber: NonEmptyText;
	#orderType: OrderType;
	#stakeholders: Stakeholder[];
	#status: Status;
	#tags: Tag[];
	#totals: Totals;
	#workflow: Workflow;
	public constructor({ currency, customer, orderDate, orderNumber, orderType, stakeholders, status, tags, totals, workflow }: IOrder) {
		assertIsInstanceOfCurrency(currency);
		this.#currency = currency;
		assertIsInstanceOfCustomer(customer);
		this.#customer = customer;
		assertIsInstanceOfDate(orderDate);
		this.#orderDate = orderDate;
		assertIsInstanceOfNonEmptyText(orderNumber);
		this.#orderNumber = orderNumber;
		assertIsInstanceOfOrderType(orderType);
		this.#orderType = orderType;
		assertIsInstanceOfArray(stakeholders);
		this.#stakeholders = stakeholders;
		assertIsInstanceOfStatus(status);
		this.#status = status;
		assertIsInstanceOfArray(tags);
		this.#tags = tags;
		assertIsInstanceOfTotals(totals);
		this.#totals = totals;
		assertIsInstanceOfWorkflow(workflow);
		this.#workflow = workflow;
	}
}
